import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import RoleAdaptiveNavbar from '../../components/ui/RoleAdaptiveNavbar';
import GalleryNavigationBreadcrumb from '../../components/ui/GalleryNavigationBreadcrumb';
import NotificationIndicator from '../../components/ui/NotificationIndicator';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import Select from '../../components/ui/Select';
import StatCard from './components/StatCard';
import GalleryCard from './components/GalleryCard';
import ShareModal from './components/ShareModal';
import AnalyticsModal from './components/AnalyticsModal';
import EmptyState from './components/EmptyState';
import Icon from '../../components/AppIcon';


const ContributorDashboard = () => {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState('recent');
  const [selectedGallery, setSelectedGallery] = useState(null);
  const [showShareModal, setShowShareModal] = useState(false);
  const [showAnalyticsModal, setShowAnalyticsModal] = useState(false);

  const mockNotifications = [
  {
    id: 1,
    type: 'visitor',
    title: 'New Gallery Visitor',
    message: 'Sarah Johnson accessed "Wedding Portfolio 2026"',
    timestamp: new Date(Date.now() - 1800000),
    read: false
  },
  {
    id: 2,
    type: 'sync',
    title: 'Google Drive Sync Complete',
    message: 'Successfully synced 45 new media files to "Corporate Events"',
    timestamp: new Date(Date.now() - 7200000),
    read: false
  },
  {
    id: 3,
    type: 'gallery',
    title: 'Download Activity',
    message: '12 files downloaded from "Product Photography" gallery',
    timestamp: new Date(Date.now() - 86400000),
    read: true
  }];


  const mockGalleries = [
  {
    id: 1,
    name: "Wedding Portfolio 2026",
    description: "Elegant wedding photography collection featuring romantic moments and stunning venue shots from premium ceremonies",
    thumbnail: "https://images.unsplash.com/photo-1600243224541-0d53bb1f872b",
    thumbnailAlt: "Elegant bride in white wedding dress holding bouquet of white roses in sunlit garden with soft bokeh background",
    mediaCount: 156,
    visitorCount: 23,
    createdDate: "2026-01-05",
    lastAccessed: "2026-01-13",
    isActive: true,
    syncStatus: 'synced',
    shareCode: 'WP2026-ABC123',
    analytics: {
      totalViews: 342,
      totalDownloads: 89,
      avgViewTime: '4m 32s',
      recentVisitors: [
      {
        id: 1,
        name: 'Sarah Johnson',
        email: 'sarah.j@email.com',
        phone: '+1 (555) 123-4567',
        lastVisit: '2026-01-13T14:30:00',
        downloads: 12
      },
      {
        id: 2,
        name: 'Michael Chen',
        email: 'mchen@company.com',
        phone: '+1 (555) 234-5678',
        lastVisit: '2026-01-12T16:45:00',
        downloads: 8
      }],

      popularDownloads: [
      { fileName: 'ceremony_first_kiss.jpg', count: 45 },
      { fileName: 'reception_dance.jpg', count: 38 },
      { fileName: 'couple_portrait.jpg', count: 32 }]

    }
  },
  {
    id: 2,
    name: "Corporate Events 2026",
    description: "Professional event photography showcasing corporate conferences, team building activities, and executive portraits",
    thumbnail: "https://images.unsplash.com/photo-1724827192883-95d0b50c81ce",
    thumbnailAlt: "Large corporate conference audience seated in modern auditorium with blue lighting and professional stage setup",
    mediaCount: 234,
    visitorCount: 45,
    createdDate: "2025-12-20",
    lastAccessed: "2026-01-12",
    isActive: true,
    syncStatus: 'syncing',
    shareCode: 'CE2026-DEF456',
    analytics: {
      totalViews: 567,
      totalDownloads: 134,
      avgViewTime: '6m 15s',
      recentVisitors: [
      {
        id: 3,
        name: 'David Martinez',
        email: 'dmartinez@corp.com',
        phone: '+1 (555) 345-6789',
        lastVisit: '2026-01-12T10:20:00',
        downloads: 23
      }],

      popularDownloads: [
      { fileName: 'keynote_speaker.jpg', count: 67 },
      { fileName: 'team_photo.jpg', count: 54 }]

    }
  },
  {
    id: 3,
    name: "Product Photography",
    description: "High-quality product shots with professional lighting and styling for e-commerce and marketing campaigns",
    thumbnail: "https://images.unsplash.com/photo-1594238543574-e94dc4572d2a",
    thumbnailAlt: "Luxury wristwatch with silver metal band and black dial photographed on white background with dramatic side lighting",
    mediaCount: 89,
    visitorCount: 12,
    createdDate: "2025-12-15",
    lastAccessed: "2026-01-10",
    isActive: true,
    syncStatus: 'synced',
    shareCode: 'PP2026-GHI789',
    analytics: {
      totalViews: 234,
      totalDownloads: 67,
      avgViewTime: '3m 45s',
      recentVisitors: [
      {
        id: 4,
        name: 'Emily Rodriguez',
        email: 'emily.r@brand.com',
        phone: '+1 (555) 456-7890',
        lastVisit: '2026-01-10T13:15:00',
        downloads: 15
      }],

      popularDownloads: [
      { fileName: 'product_hero_shot.jpg', count: 34 },
      { fileName: 'lifestyle_context.jpg', count: 28 }]

    }
  },
  {
    id: 4,
    name: "Fashion Editorial 2026",
    description: "Contemporary fashion photography featuring bold styling, dramatic poses, and urban locations for magazine spreads",
    thumbnail: "https://images.unsplash.com/photo-1613340456954-d32029c98d94",
    thumbnailAlt: "Fashion model in elegant black evening gown posing against urban concrete wall with dramatic natural lighting and confident stance",
    mediaCount: 178,
    visitorCount: 34,
    createdDate: "2025-12-01",
    lastAccessed: "2026-01-08",
    isActive: false,
    syncStatus: 'synced',
    shareCode: 'FE2026-JKL012',
    analytics: {
      totalViews: 445,
      totalDownloads: 98,
      avgViewTime: '5m 20s',
      recentVisitors: [
      {
        id: 5,
        name: 'Alexandra Kim',
        email: 'alex.kim@magazine.com',
        phone: '+1 (555) 567-8901',
        lastVisit: '2026-01-08T11:30:00',
        downloads: 19
      }],

      popularDownloads: [
      { fileName: 'editorial_cover.jpg', count: 52 },
      { fileName: 'street_style.jpg', count: 41 }]

    }
  }];


  const sortOptions = [
  { value: 'recent', label: 'Most Recent' },
  { value: 'oldest', label: 'Oldest First' },
  { value: 'name', label: 'Name (A-Z)' },
  { value: 'visitors', label: 'Most Visitors' },
  { value: 'media', label: 'Most Media' }];


  const breadcrumbItems = [
  { label: 'Dashboard', icon: 'LayoutDashboard' }];


  const filteredGalleries = mockGalleries?.filter((gallery) =>
  gallery?.name?.toLowerCase()?.includes(searchQuery?.toLowerCase()) ||
  gallery?.description?.toLowerCase()?.includes(searchQuery?.toLowerCase())
  )?.sort((a, b) => {
    switch (sortBy) {
      case 'oldest':
        return new Date(a.createdDate) - new Date(b.createdDate);
      case 'name':
        return a?.name?.localeCompare(b?.name);
      case 'visitors':
        return b?.visitorCount - a?.visitorCount;
      case 'media':
        return b?.mediaCount - a?.mediaCount;
      case 'recent':
      default:
        return new Date(b.createdDate) - new Date(a.createdDate);
    }
  });

  const totalMediaFiles = mockGalleries?.reduce((sum, g) => sum + g?.mediaCount, 0);
  const totalVisitors = mockGalleries?.reduce((sum, g) => sum + g?.visitorCount, 0);
  const totalDownloads = mockGalleries?.reduce((sum, g) => sum + g?.analytics?.totalDownloads, 0);
  const activeGalleries = mockGalleries?.filter((g) => g?.isActive)?.length;

  const handleShare = (gallery) => {
    setSelectedGallery(gallery);
    setShowShareModal(true);
  };

  const handleEdit = (gallery) => {
    navigate('/gallery-management', { state: { galleryId: gallery?.id } });
  };

  const handleViewAnalytics = (gallery) => {
    setSelectedGallery(gallery);
    setShowAnalyticsModal(true);
  };

  const handleNotificationClick = (notification) => {
    console.log('Notification clicked:', notification);
  };

  const handleMarkAsRead = (notificationId) => {
    console.log('Mark as read:', notificationId);
  };

  const handleMarkAllAsRead = () => {
    console.log('Mark all as read');
  };

  return (
    <>
      <Helmet>
        <title>Contributor Dashboard - Radiavision Gallery</title>
        <meta name="description" content="Manage your photography and video galleries, track visitor engagement, and share your work with clients through Radiavision Studio's contributor dashboard." />
      </Helmet>
      <div className="min-h-screen bg-background">
        <RoleAdaptiveNavbar userRole="contributor" userName="Contributor" />

        <div className="pt-[64px]">
          <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-8 py-6 md:py-8">
            <div className="flex items-center justify-between mb-6">
              <GalleryNavigationBreadcrumb items={breadcrumbItems} userRole="contributor" />
              <NotificationIndicator
                notifications={mockNotifications}
                onNotificationClick={handleNotificationClick}
                onMarkAsRead={handleMarkAsRead}
                onMarkAllAsRead={handleMarkAllAsRead} />

            </div>

            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 mb-6 md:mb-8">
              <div>
                <h1 className="text-2xl md:text-3xl lg:text-4xl font-semibold mb-2">My Galleries</h1>
                <p className="text-sm md:text-base text-muted-foreground">
                  Manage your portfolio and track client engagement
                </p>
              </div>
              <Button
                variant="default"
                size="lg"
                iconName="Plus"
                iconPosition="left"
                onClick={() => navigate('/gallery-creation')}
                className="w-full lg:w-auto">

                Create New Gallery
              </Button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6 mb-6 md:mb-8">
              <StatCard
                title="Total Galleries"
                value={mockGalleries?.length}
                icon="Images"
                trend="up"
                trendValue="+2" />

              <StatCard
                title="Total Visitors"
                value={totalVisitors}
                icon="Users"
                trend="up"
                trendValue="+12" />

              <StatCard
                title="Media Files"
                value={totalMediaFiles}
                icon="FileImage"
                trend="up"
                trendValue="+45" />

              <StatCard
                title="Downloads"
                value={totalDownloads}
                icon="Download"
                trend="up"
                trendValue="+23" />

            </div>

            {mockGalleries?.length > 0 ?
            <>
                <div className="flex flex-col md:flex-row gap-4 mb-6">
                  <div className="flex-1">
                    <Input
                    type="search"
                    placeholder="Search galleries..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e?.target?.value)} />

                  </div>
                  <div className="w-full md:w-64">
                    <Select
                    options={sortOptions}
                    value={sortBy}
                    onChange={setSortBy}
                    placeholder="Sort by" />

                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
                  {filteredGalleries?.map((gallery) =>
                <GalleryCard
                  key={gallery?.id}
                  gallery={gallery}
                  onShare={handleShare}
                  onEdit={handleEdit}
                  onViewAnalytics={handleViewAnalytics} />

                )}
                </div>

                {filteredGalleries?.length === 0 &&
              <div className="text-center py-12">
                    <Icon name="Search" size={48} color="var(--color-muted-foreground)" className="mx-auto mb-4" />
                    <p className="text-muted-foreground">No galleries found matching your search</p>
                  </div>
              }
              </> :

            <EmptyState />
            }
          </div>
        </div>

        {showShareModal && selectedGallery &&
        <ShareModal
          gallery={selectedGallery}
          onClose={() => {
            setShowShareModal(false);
            setSelectedGallery(null);
          }} />

        }

        {showAnalyticsModal && selectedGallery &&
        <AnalyticsModal
          gallery={selectedGallery}
          onClose={() => {
            setShowAnalyticsModal(false);
            setSelectedGallery(null);
          }} />

        }
      </div>
    </>);

};

export default ContributorDashboard;